import { create } from 'zustand';

interface ScrollLockState {
    scrollLocked: boolean;
    /** Internal — scroll position saved by lockScroll(), restored by unlockScroll(). */
    _savedScrollY: number;
    actions: {
        /** Freezes the body in place (position: fixed) to prevent background scroll while a modal/popover is open. */
        lockScroll: () => void;
        /** Restores normal scrolling and the exact scroll position saved by lockScroll(). */
        unlockScroll: () => void;
    };
}

export const useScrollLockStore = create<ScrollLockState>()((set, get) => ({
    scrollLocked: false,
    _savedScrollY: 0,
    actions: {
        lockScroll: () => {
            if (get().scrollLocked) {
                return;
            }

            // During SSR (or document teardown) there is no window/body to lock.
            if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) {
                return;
            }

            const savedScrollY = Math.round(window.scrollY);

            const body = document.body;
            body.style.position = 'fixed';
            body.style.top = `-${savedScrollY}px`;
            body.style.left = '0';
            body.style.right = '0';
            body.style.width = '100%';

            set({ scrollLocked: true, _savedScrollY: savedScrollY });
        },
        unlockScroll: () => {
            if (!get().scrollLocked) {
                return;
            }

            // If the document is gone (SSR, teardown mid-navigation), there is nothing to
            // restore — but still clear the flag so the store can't get stuck locked.
            if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) {
                set({ scrollLocked: false });
                return;
            }

            const scrollY = get()._savedScrollY;

            const body = document.body;
            body.style.position = '';
            body.style.top = '';
            body.style.left = '';
            body.style.right = '';
            body.style.width = '';

            window.scrollTo(0, scrollY);
            set({ scrollLocked: false });
        },
    },
}));
